// ========================= IMPORTS =========================
// React and Redux imports
import React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import PropTypes from "prop-types";
import _ from "lodash";
import idx from "idx";
import { v4 as uuidv4 } from "uuid";
// Redux actions for inventory and common notifications
import {
  getInventoryLists,
  getUnits,
  addItem,
  listCategories,
  getVendorCategories,
} from "../actions/inventory.action";
import {
  warningSnackBar,
  errorSnackBar,
  successSnackBar,
} from "../actions/common.action";
// Utility and constants
import { getFormatedUnit } from "./index";
import {
  itemFields,
  categoriesFields,
  categoryItemFields,
} from "../constants/fields.constant";
// Dialog and UI components
import AddItemDialog from "../routes/projects/add-item-dialog";
import Table from "react-bootstrap/Table";
import {
  filterManufacturer,
  filterItems,
  filterPartNo,
  debounceTime,
  parseNumericValue,
  parseFloatValue,
  isInlineItem as getIsInlineItem,
  INLINE_CATALOG_DISPLAY_NAME,
} from "./common";
import "../routes/projects/scss/project-management.scss";
import "../routes/projects/scss/task.scss";
import SaveIcon from "@mui/icons-material/Save";
import Button from "react-bootstrap/Button";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import AddIcon from "@mui/icons-material/Add";
import GetAppIcon from "@mui/icons-material/GetApp";
import CustomInput, { NumberInput } from "../components/inputs/new-input";
import SelectInput from "../components/inputs/select";
import { toUSD, numberFormat } from "./currency-formatter";
import { debounce } from "@mui/material/utils";
import Tooltip from "@mui/material/Tooltip";
import ItemSelectionOverlay from "../components/item-selection-overlay/item-selection-overlay";
import Popper from "@mui/material/Popper";
import "../components/po/create.scss";
import { Delete } from "@mui/icons-material";
import {
  downloadProjectRFQ,
  saveRFQ,
} from "src/actions/project-management.action";
import { getGlobalSettingTemplateTypes } from "src/actions/global-setting-template.action";
import DownloadDialog from "src/components/download-dialog";
import TaskMultiSelect from "src/components/inputs/task-multi-select";
import PaginationComponent from "../components/table/pagination";

// ========================= FILTER OPTIONS =========================
const filter = createFilterOptions();

// ========================= MAIN COMPONENT =========================
/**
 * ADD-MATERIAL-PO.JSX - Purchase Order Material Management Component
 * 
 * SUMMARY:
 * Comprehensive component for managing materials in purchase orders. Handles material 
 * selection, inline item creation, item overlay functionality, and purchase order consolidation
 * with support for task-based material allocation.
 * 
 * @component
 * @param {Object} props.poData - Purchase order data including projectId, vendorId, etc.
 * @param {Array} props.tasksList - Available tasks/stages for material allocation
 * @param {Array} props.taskItems - Pre-populated material items from external source
 * @param {Function} props.onItemRemove - Callback when item is removed
 * @param {Object} props.permissions - User permissions for catalog operations
 */
class AddMaterialPO extends React.Component {
  /**
   * Initialize state and refs for AddMaterialPO
   */
  constructor(props) {
    super(props);
    this.state = {
      // List of available categories for selection
      categoriesList: [],
      // List of available items for selection
      itemsList: [],
      // Template for a single item
      item: itemFields,
      // List of available units
      newunitList: [],
      // Currently selected unit value
      newunitvalue: "",
      // Total value for the new item
      newtotalvalue: 0,
      // ID for the new item
      newitemid: "",
      // Whether a new item is being added
      newItem: false,
      // Checkbox state for some UI toggle
      checked: true,
      // List of selected category names
      categoryName: [],
      // List of selected items
      selectedItems: [],
      // List of items in the selected category
      categoryItems: [],
      // UI state for showing add item dialog
      showAddItem: [],
      addItemDialogState: false,
      // Category and item field templates
      categories: JSON.parse(JSON.stringify(categoriesFields)),
      categoryItem: JSON.parse(JSON.stringify(categoryItemFields)),
      // Currently selected category ID
      categoryId: "",
      // PO data passed as prop
      poData: {},
      // Inline item editing state
      isInlineItem: false,
      inlineItemIndex: -1,
      // Value objects for item and filter
      itemValue: {},
      filterValue: {},
      // Search fields
      search: "",
      partNoSearch: "",
      // Modal and overlay UI state
      itemSelectionModalOpen: false,
      availableItems: [],
      selectedItemIds: [],
      isLoadingItems: false,
      currentItemIndex: -1,
      page: 0,
      rowsPerPage: 10,
      modalSearchQuery: "",
      downloadData: {},
      originalAvailableItems: [],
      searchModalTimer: null,
      overlayOpen: false,
      overlayAnchorEl: null,
      overlayItemIndex: -1,
      isProcessingInternalUpdate: false,
      tablePage: 1,
    };
    // Debounced item fetching for search
    this.debouncedGetItems = debounce(
      this.getItems,
      debounceTime.inventoryList,
    );
    this.debouncedSearchModalItems = debounce(this.searchModalItems, 300);
    this.searchModalTimer = null;
    this.modalSearchTimer = null;
    this.lastProcessedTaskItems = null;
    // Create a single container for all refs
    this.inputRefs = {};
  }

  // ========================= REF SETTERS =========================
  // Set ref for catalog input
  setCatalogInputRef = (index, element) => {
    this.catalogInputRefs[index] = element;
  };

  // Set ref for item input
  setItemInputRef = (index, element) => {
    this.itemInputRefs[index] = element;
  };

  // ========================= LIFECYCLE METHODS =========================
  // Fetch categories and initialize items on mount
  async componentDidMount() {
    await this.props.listCategories();
    await this.init();
  }

  // Initialize items and units
  init = async () => {
    await this.fetchItems();
  };

  // Fetch items and units, update state with available categories and units
  fetchItems = async (filter) => {
    let { categories, item } = this.state;
    let { poData, categoryList } = this.props;

    if (categoryList.length == 0) await this.props.listCategories();
    const categoriesList = categoryList.map((category) => ({
      key: category.name,
      name: category.name,
      value: category.id,
    }));
    categories.items = categoriesList;
    this.getItems();

    const unitsResult = await this.props.getUnits({ sortBy: "name" });
    const allUnits = idx(unitsResult, (_unitsRes) => _unitsRes[0].units) || [];
    const units = getFormatedUnit(allUnits);
    item.unit.items = units;

    this.handleRFQPurchaseOrder(poData);
    const newunitList = units;
    this.setState({ categories, categoriesList, item, allUnits, newunitList });
  };

  // Set checked state for checkbox UI
  setChecked = (checked) => {
    this.setState({ checked: checked });
  };

  /**
   * Sets the current page for table pagination.
   * Updates the tablePage state which controls which rows are visible in the paginated table.
   * 
   * @param {number} pageNumber - The page number to navigate to (1-based index)
   */
  setTablePage = (pageNumber) => {
    this.setState({ tablePage: pageNumber });
  };

  /**
   * Handles changes to the rows per page setting.
   * Updates the rowsPerPage state and resets to page 1 to avoid out-of-bounds issues.
   * 
   * @param {Event} e - The change event from the select input
   */
  handleRowsPerPageChange = (e) => {
    const newRowsPerPage = parseInt(e.target.value, 10);
    this.setState({
      rowsPerPage: newRowsPerPage,
      tablePage: 1, // Reset to first page when changing page size
    });
  };

  // React lifecycle: update when taskItems prop changes
  componentDidUpdate(prevProps) {
    if (!_.isEqual(prevProps.taskItems, this.props.taskItems)) {
      if (this.state.isProcessingInternalUpdate) {
        // During removal: sync tracker without calling handleTaskPO, so the next "Add All" is processed correctly.
        this.lastProcessedTaskItems = _.cloneDeep(this.props.taskItems);
        return;
      }

      if (!_.isEqual(this.lastProcessedTaskItems, this.props.taskItems)) {
        this.lastProcessedTaskItems = _.cloneDeep(this.props.taskItems);
        this.handleTaskPO(this.props.taskItems);
      }
    }
  }

  // ========================= ITEM FETCHING =========================
  // Fetch items based on current search and filter criteria
  getItems = async () => {
    let { search, filterValue, partNoSearch } = this.state;

    let categoryFilter =
      filterValue && filterValue.catalog ? filterValue.catalog : "";
    let categoryId =
      filterValue && filterValue.categoryId ? filterValue.categoryId : "";
    let manufacturerFilter =
      filterValue && filterValue.manufacturer ? filterValue.manufacturer : "";
    let partNoFilter =
      filterValue && filterValue.partNo
        ? filterValue.partNo
        : partNoSearch || "";
    let params = {};
    let itemsList = [];
    params = {
      ...(categoryId ? { categoryId } : { categoryFilter }),
      manufacturerFilter,
      search,
      partNoFilter,
    };
    if (params.categoryId || params.search || params.partNoFilter) {
      const inventoryList = await this.props.getInventoryLists(params);
      itemsList = inventoryList.category;
      this.setState({ itemsList });
    }
  };

  // Handle changes in the search input field
  handleInputChange = (event, newInputValue) => {
    const searchValue =
      newInputValue !== undefined
        ? newInputValue
        : event && event.target
          ? event.target.value
          : "";

    this.setState({ search: searchValue });

    if (searchValue && searchValue.length > 0) {
      this.debouncedGetItems();
    }

    return searchValue;
  };

  // ========================= TASK AND PO HANDLING =========================
  // Group task items by item and part number, aggregating quantities
  groupByItemAndPartNo = (taskItems) => {
    const grouped = _.groupBy(
      taskItems,
      (item) => `${item.taskId}_${item.id}_${item.cost}`,
    );
    const aggregated = _.map(grouped, (items) => ({
      ...items[0],
      qty: _.sumBy(items, (i) => parseFloatValue(i.qty || i.qty?.value || 0)),
    }));
    return aggregated;
  };

  /**
   * Checks whether target item already exists in existingList by unique identifying key:
   * (task/stage, material item, and qty).
   * @param {Object} target - The item to check
   * @param {Array} existingList - The list of existing items
   * @param {number} [excludeIndex=-1] - Index to exclude from comparison
   * @returns {boolean}
   */
  isDuplicateLineItem = (target, existingList = this.state.selectedItems, excludeIndex = -1) => {
    if (!target) return false;
    const targetItemId = String(target.id || '');
    if (!targetItemId) return false;

    const targetQty = Number(target.qty?.value !== undefined ? target.qty.value : target.qty) || 0;
    const targetTaskId = String(target.taskId || target.stageId || '');
    if (!targetTaskId) return false;

    return (existingList || []).some((exItem, exIdx) => {
      if (exIdx === excludeIndex) return false;
      const exItemId = String(exItem.id || '');
      if (exItemId !== targetItemId) return false;

      const exQty = Number(exItem.qty?.value !== undefined ? exItem.qty.value : exItem.qty) || 0;
      if (exQty !== targetQty) return false;

      const exTaskId = String(exItem.taskId || exItem.stageId || '');
      return exTaskId === targetTaskId;
    });
  };

  // Handle updates to the task PO based on selected task items
  handleTaskPO = (taskItems = []) => {
    let { selectedItems, item, itemValue } = this.state;

    if (Array.isArray(taskItems) && taskItems.length) {
      let duplicateCount = 0;
      taskItems.forEach((_item, index) => {
        const itemQty = parseNumericValue(numberFormat(_item.qty, true)) || 1;
        const targetToCheck = {
          id: _item.id,
          taskId: _item.taskId,
          stageId: _item.stageId,
          qty: itemQty,
        };

        if (this.isDuplicateLineItem(targetToCheck, selectedItems)) {
          duplicateCount++;
          return;
        }

        const itemValueKey = `${_item.id}_${index}`;
        itemValue[itemValueKey] = {
          key: _item.item,
          value: _item.id,
          index: index,
        };
        const newItem = JSON.parse(JSON.stringify(item));
        newItem.taskName = _item.taskName;
        newItem.taskDescription = _item.taskDescription;
        newItem.taskType = _item.taskType;
        newItem.taskId = _item.taskId;
        newItem.stageId = _item.stageId;
        newItem.task = {
          error: "",
          valid: true,
        };
        newItem.name.value = _item.item;
        newItem.id = _item.id;
        newItem.manufacturer = _item.manufacturer;
        newItem.partNo = _item.partNo || _item.part_no;
        newItem.categoryId = _item.categoryId;
        newItem.costPerItem.value = parseNumericValue(_item.cost);
        newItem.costPerItem.valid = true;
        newItem.costPerItem.error = "";
        newItem.unit.value = _item.unit;
        newItem.qty.value = itemQty;
        newItem.qty.valid = true;
        newItem.qty.error = "";
        newItem.total.value = newItem.qty.value * newItem.costPerItem.value;
        newItem.isInlineItem = _item.isInlineItem || false;

        selectedItems.push(newItem);
      });

      if (duplicateCount > 0) {
        this.props.warningSnackBar(
          "An item with the same task, item and quantity already exists in the PO."
        );
      }
    }
    this.setState({ selectedItems, itemValue });
  };

  // Handle updates to the RFQ Purchase Order based on provided PO data
  handleRFQPurchaseOrder = (poData = {}) => {
    let { selectedItems, item, itemValue } = this.state;

    const rfqItems = idx(poData, (_poData) => _poData.items) || [];

    if (Array.isArray(rfqItems) && rfqItems.length) {
      rfqItems.forEach((_item, index) => {
        const itemQty = parseNumericValue(numberFormat(_item.qty, true)) || 1;
        const targetToCheck = {
          id: _item.id,
          taskId: _item.taskId,
          stageId: _item.stageId,
          qty: itemQty,
        };
        if (this.isDuplicateLineItem(targetToCheck, selectedItems)) {
          return; // Skip if the item already exists
        }
        const itemValueKey = `${_item.id}_${index}`;
        itemValue[itemValueKey] = {
          key: _item.item,
          value: _item.id,
        };

        const newItem = JSON.parse(JSON.stringify(item));
        newItem.taskName = "";
        newItem.taskDescription = "";
        newItem.taskType = "";
        newItem.taskId = _item.taskId || "";
        newItem.stageId = _item.stageId || "";
        newItem.task = {
          error: "",
          valid: true,
        };
        newItem.name.value = _item.item;
        newItem.id = _item.id;
        newItem.manufacturer = _item.manufacturer;
        newItem.partNo = _item.partNo || _item.part_no;
        newItem.categoryId = _item.categoryId;
        newItem.costPerItem.value = parseNumericValue(_item.cost);
        newItem.costPerItem.valid = true;
        newItem.costPerItem.error = "";
        newItem.unit.value = _item.unit;
        newItem.qty.value = parseNumericValue(numberFormat(_item.qty, true));
        newItem.qty.valid = true;
        newItem.qty.error = "";
        newItem.total.value = newItem.qty.value * newItem.costPerItem.value;
        newItem.isInlineItem = _item.isInlineItem || false;

        selectedItems.push(newItem);

        if (_item.unit) {
          this.setState((prev) => ({
            ...prev,
            newunitList: _.uniqBy(
              [
                ...prev.newunitList,
                ...[{ key: _item.unit, value: _item.unit }],
              ],
              "key",
            ),
          }));
        }
      });
    }

    this.setState({ selectedItems, itemValue });
  };

  // ========================= HANDLERS =========================
  // Handle changes in task selection
  changeTaskHandler = (index = -1, selectedOption) => {
    let { selectedItems, item } = this.state;
    const { tasksList = [] } = this.props;

    // Handle single selection from the multi-select (get the first selected option)
    const selectedTask =
      Array.isArray(selectedOption) && selectedOption.length > 0
        ? selectedOption[0]
        : selectedOption;

    if (!selectedTask || !selectedTask.value) return;

    const _task =
      tasksList.find((_task) => _task.value === selectedTask.value) || {};

    if (index === -1) {
      const newItem = JSON.parse(JSON.stringify(item));
      newItem.taskName = _task.label || _task.key;
      newItem.taskDescription = _task.taskDescription;

      // Check if selected option is a stage (attributed section)
      if (selectedTask.isStage) {
        newItem.taskType = "STAGE"; // ← Force to STAGE for attributed sections
        newItem.isStage = true;
      } else {
        newItem.taskType = _task.taskType || "TASK"; // ← Use original or default to TASK
        newItem.isStage = false;
      }
      newItem.taskId = selectedTask.value;
      newItem.task = {
        error: "",
        valid: true,
      };
      newItem.categoryId = "";
      newItem.name.value = "";
      newItem.manufacturer = "";
      newItem.partNo = "";
      newItem.costPerItem.value = "";
      newItem.unit.value = "";
      newItem.qty.value = "";
      newItem.total.value = "";
      selectedItems.push(newItem);
    } else if (index > -1) {
      if (Array.isArray(selectedItems) && selectedItems.length > index) {
        selectedItems[index].taskName = _task.label || _task.key;
        selectedItems[index].taskDescription = _task.taskDescription;
        // Apply intelligent attribution based on isStage flag
        if (selectedTask.isStage) {
          selectedItems[index].taskType = "STAGE"; // ← Force to STAGE for attributed sections
          selectedItems[index].isStage = true;
        } else {
          selectedItems[index].taskType = _task.taskType || "TASK"; // ← Use original or default to TASK
          selectedItems[index].isStage = false;
        }
        selectedItems[index].taskId = selectedTask.value;
        selectedItems[index].taskId = selectedTask.value;
        selectedItems[index].task = {
          error: "",
          valid: true,
        };
      }
    }
    this.setState({ selectedItems });
  };

  // Handle changes in category selection
  handleCategoryChange = (
    index = -1,
    categoryId = "",
    itemId = "",
    key = "",
  ) => {
    let { selectedItems, itemsList, categoryItems, itemValue, filterValue } =
      this.state;
    if (
      index > -1 &&
      Array.isArray(selectedItems) &&
      selectedItems.length > index
    ) {
      selectedItems[index].categoryId = categoryId;
      selectedItems[index].catalog = key;
      selectedItems[index].id = "";
      selectedItems[index].qty.value = "";
      selectedItems[index].costPerItem.value = "";
      const categoryItemList =
        Array.isArray(itemsList) &&
        itemsList
          .filter((item) => item.categoryId === categoryId)
          .map((categoryItem) => ({
            key: categoryItem.item,
            value: categoryItem.id,
          }));
      categoryItems[categoryId] = categoryItemList;
      filterValue.categoryId = categoryId;
    }
    if (itemId) {
      const itemValueKey = `${itemId}_${index}`;
      delete itemValue[itemValueKey];
    }
    this.setState({ selectedItems, categoryItems, itemValue, filterValue });
  };

  // Toggle the add item dialog state
  AddItemDialogHandler = (index, item = "", categoryId = "") => {
    this.setState((prevState) => ({
      addItemDialogState: !prevState.addItemDialogState,
      categoryId: categoryId,
      itemName: item,
      inlineItemIndex: index,
    }));
  };

  // Close the add item dialog and handle the response
  AddItemDialogClose = (res = {}, inlineItem = false) => {
    if (!_.isEmpty(res) && !inlineItem) {
      const { itemsList, inlineItemIndex } = this.state;
      res.categoryId = res.category_id;
      itemsList.push(res);
      this.setState({ itemsList, filterValue: {} });
      this.handleAddItem(inlineItemIndex, { value: res.id });
    } else if (!_.isEmpty(res) && inlineItem) {
      this.AddInlineItem(res);
    }
    this.setState({
      addItemDialogState: false,
      isInlineItem: false,
      inlineItemIndex: -1,
    });
  };

  // ========================= FILTERING =========================
  // Add a filter criterion and update the state
  addFilter = (value, type) => {
    let { filterValue } = this.state;
    if (!filterValue) filterValue = {};
    if (filterValue && filterValue[type] !== value) {
      filterValue[type] = value;
      if (filterValue.noresult) {
        if (type == "unit") {
          const newunitvalue = value;
          this.setState({ newunitvalue });
        }
        if (type === "costPerItem" || type === "qty") {
          const newtotalvalue =
            parseInt(filterValue["qty"]) *
            parseFloat(filterValue["costPerItem"]);
          this.setState({ newtotalvalue });
        }
      }
      this.setState({ filterValue });
      this.itemsDropDownOption(true);
      this.getItems();
    }
  };

  // Generate dropdown options for part numbers based on current items and filters
  partNoDropDown = () => {
    const { filterValue, itemsList, partNoSearch } = this.state;
    let filterOptions = filterPartNo(itemsList, filterValue);

    if (filterOptions.length === 0 && partNoSearch && partNoSearch.length > 2) {
      filterOptions = [
        {
          inputValue: partNoSearch,
          key: partNoSearch,
          value: partNoSearch,
        },
      ];
    }

    const orderedFilterOptions = _.orderBy(filterOptions, ["value"], ["asc"]);
    return orderedFilterOptions;
  };

  // Generate dropdown options for manufacturers based on current items and filters
  manufacturerDropDown = () => {
    const { filterValue, itemsList } = this.state;
    let filterOptions = filterManufacturer(itemsList, filterValue);
    if (filterOptions.length === 0) {
      this.setFilterResult(true, "");
    } else {
      this.setFilterResult(false, "");
    }
    return filterOptions;
  };

  // Generate dropdown options for items based on current items and filters
  itemsDropDownOption = () => {
    const { filterValue, itemsList, search } = this.state;
    let filterOptions = filterItems(itemsList, filterValue);

    if (filterOptions.length === 0) {
      filterOptions = [
        {
          inputValue: search,
          key: `Add "${search}"`,
          value: null,
        },
      ];
      this.setFilterResult(true, "");
    } else {
      this.setFilterResult(false, "");
    }
    return filterOptions;
  };

  // Set the result of the filter operation (e.g., no results found)
  setFilterResult = (value, msg) => {
    const { filterValue } = this.state;
    let prevValue = idx(
      filterValue,
      (_filterValue) => _filterValue["noresult"],
    );
    if (prevValue !== value) {
      filterValue["noresult"] = value;
      filterValue["noresult_msg"] = msg;
      this.setState({ filterValue });
    }
  };

  // ========================= ITEM ADDITION =========================
  // Handle adding a new item or opening the dialog to add a new item
  handleAddItem = (index = -1, newValue = {}) => {
    let { selectedItems, itemsList, itemValue } = this.state;
    const itemId = newValue.value;

    if (newValue.value === null) {
      this.AddItemDialogHandler(
        index,
        this.state.search,
        this.state.filterValue.categoryId,
      );
    } else {
      if (
        index > -1 &&
        Array.isArray(selectedItems) &&
        selectedItems.length > index
      ) {
        if (idx(selectedItems, (_selectedItems) => _selectedItems[index].id)) {
          delete itemValue[
            idx(selectedItems, (_selectedItems) => _selectedItems[index].id)
          ];
        }
        const item =
          (Array.isArray(itemsList) &&
            itemsList.find((_item) => _item.id === itemId)) ||
          {};

        // Check if an item with identical task, section, item and quantity already exists in another row
        const targetToCheck = {
          id: itemId,
          taskId: selectedItems[index]?.taskId,
          stageId: selectedItems[index]?.stageId,
          qty: selectedItems[index]?.qty?.value !== undefined ? selectedItems[index].qty.value : (item.qty || 1),
        };

        if (this.isDuplicateLineItem(targetToCheck, selectedItems, index)) {
          this.props.warningSnackBar("An item with the same task, item and quantity already exists in the PO.");
          return;
        }

        const itemValueKey = `${itemId}_${index}`;
        if (itemValue[itemValueKey]) {
          this.props.warningSnackBar("Item Already Selected!");
          return;
        }
        itemValue[itemValueKey] = newValue;
        selectedItems[index].name.value = item.item;
        selectedItems[index].id = item.id;
        selectedItems[index].manufacturer = item.manufacturer;
        selectedItems[index].partNo = item.partNo || item.part_no;
        selectedItems[index].categoryId = item.categoryId;
        selectedItems[index].costPerItem.value = parseNumericValue(item.cost);
        selectedItems[index].costPerItem.valid = true;
        selectedItems[index].costPerItem.error = "";
        selectedItems[index].unit.value = item.unit;
        selectedItems[index].qty.value = item.qty || 0;
        selectedItems[index].qty.valid = true;
        selectedItems[index].qty.error = "";
        selectedItems[index].total.value = item.total || 0;
        selectedItems[index].isInlineItem = item.isInlineItem || false;
        if (item.unit) {
          this.setState((prev) => ({
            ...prev,
            newunitList: _.uniqBy(
              [...prev.newunitList, ...[{ key: item.unit, value: item.unit }]],
              "key",
            ),
          }));
        }
      }

      this.setState({ selectedItems, itemValue, filterValue: {} });
    }
  };

  // Remove an item from the selected items list
  removeItem = (index = -1) => {
    let { selectedItems, itemValue } = this.state;

    if (
      index > -1 &&
      Array.isArray(selectedItems) &&
      selectedItems.length > index
    ) {
      const itemToRemove = selectedItems[index];
      const itemId = itemToRemove?.id;
      const taskId = itemToRemove
        ? itemToRemove.stageId || itemToRemove.taskId
        : undefined;

      selectedItems.splice(index, 1);

      if (itemId) {
        const itemValueKey = `${itemId}_${index}`;
        delete itemValue[itemValueKey];
      }
      // Set flag to prevent componentDidUpdate from re-processing
      this.setState(
        {
          selectedItems: [...selectedItems],
          itemValue: { ...itemValue },
          isProcessingInternalUpdate: true,
        },
        () => {
          // Pass taskId so parent removes only this (itemId + taskId) row, not all rows sharing the same catalog id.
          if (this.props.onItemRemove && itemId) {
            this.props.onItemRemove(itemId, taskId);
          }

          // Reset the flag after state update
          setTimeout(() => {
            this.setState({ isProcessingInternalUpdate: false });
          }, 100);
        },
      );
    }
  };

  // Handle changes in item fields (cost, quantity, etc.)
  itemChangeHandler = (index, e) => {
    const { selectedItems, filterValue } = this.state;

    if (
      index > -1 &&
      Array.isArray(selectedItems) &&
      selectedItems.length > index
    ) {
      if (e.target.name === "costPerItem" || e.target.name === "qty") {
        if (e.target.value < 0) return;

        // For costPerItem, validation for money inputs
        if (
          e.target.name === "costPerItem" &&
          typeof e.target.value === "string"
        ) {
          // Remove any non-numeric characters except decimal point
          const cleanValue = e.target.value.replace(/[^\d.]/g, "");

          // Check if it's a valid number with up to 2 decimal places
          const regex = /^(\d+(\.\d{0,2})?|\.\d{0,2})$/;
          if (cleanValue === "" || regex.test(cleanValue)) {
            selectedItems[index][e.target.name].value = cleanValue;
          } else {
            // Reject the change
            return;
          }
        } else {
          selectedItems[index][e.target.name].value = e.target.value;
        }
      } else {
        selectedItems[index][e.target.name].value = e.target.value;
      }

      if (e.target.name === "unit") {
        const newunitvalue = e.target.value;
        filterValue.unit = e.target.value;
        this.setState({ newunitvalue });
      }

      selectedItems[index][e.target.name].error = "";
      selectedItems[index][e.target.name].valid = true;

      // Recalculate total
      const costVal = selectedItems[index]["costPerItem"].value;
      const cost = parseFloat(costVal) || 0;
      const qty = parseFloat(selectedItems[index]["qty"].value) || 0;
      selectedItems[index]["total"].value = qty * cost;
    }
    this.setState({ selectedItems, filterValue });
  };
  // ========================= UI TRIGGERS =========================
  // Show the add item UI or trigger validation and addition of inline items
  showAddItem = () => {
    if (this.state.filterValue.noresult) {
      if (
        !this.state.checked &&
        this.state.filterValue.categoryId &&
        !this.state.filterValue.id
      ) {
        return this.props.errorSnackBar("Please select an Item");
      }
      if (this.state.checked && !this.state.filterValue.categoryId) {
        return this.props.errorSnackBar("Please select a Catalogue");
      }
      if (this.state.checked && this.state.filterValue.categoryId) {
        this.submitItemForm();
      }
      if (!this.state.checked && !this.state.filterValue.categoryId) {
        this.AddInlineItem(this.state.filterValue);
        this.props.successSnackBar("Task Item Added Successfully");
      }
    }
    const filterValue = {};
    this.setState({
      showAddItem: true,
      filterValue,
      newtotalvalue: 0,
      newunitvalue: "",
    });
  };

  // Submit the form with selected items, showing validation errors if any
  submitForm = () => {
    const { selectedItems } = this.state;
    const { isSkippable = false, tasksList = [] } = this.props;

    if (!isSkippable && Array.isArray(selectedItems) && !selectedItems.length) {
      this.props.warningSnackBar("Select task and item to Generate PO!");
    } else if (Array.isArray(selectedItems)) {
      let isFormValid = true;
      const invalidItems = []; // Track which items are invalid
      const updatedItems = selectedItems.map((_item, index) => {
        // Check if this is an inline/one-time item (no categoryId, no id, has essential fields)
        const isOneTimeItem =
          !_item.categoryId &&
          !_item.id &&
          _item.name.value &&
          _item.costPerItem.value &&
          _item.qty.value;

        // Generate ID for inline items that don't have one
        if (isOneTimeItem && !_item.id) {
          _item.id = uuidv4();
          _item.isInlineItem = true;
          _item.categoryId = "INLINE_ITEM";
          _item.catalog = "INLINE_ITEM";
        }

        // Ensure taskName is populated from taskId if missing
        if (_item.taskId) {
          const task = tasksList.find((task) => task.value === _item.taskId);

          // DEBUG: Add this log

          if (task) {
            // Always update taskName if missing
            if (!_item.taskName) {
              _item.taskName = task.label || task.key;
            }

            // Apply intelligent attribution logic
            if (_item.isStage || task.isStage) {
              _item.stageId = _item.taskId; // ← Item-level truth
              _item.taskType = "STAGE";
            } else {
              _item.taskType = "TASK";
              _item.stageId = null;
            }

            _item.taskDescription =
              task.taskDescription || _item.taskDescription;
          }
        }
        // Also update existing items that might have missing taskType
        if (_item.taskId && _item.isStage) {
          _item.taskType = "STAGE"; // ← Force stage attribution
        } else if (_item.taskId && !_item.isStage) {
          _item.taskType = _item.taskType || "TASK"; // ← Ensure task attribution
        } else if (_item.taskId && !_item.isStage) {
          // For items without isStage flag, check tasksList
          const task = tasksList.find((task) => task.value === _item.taskId);
          if (task && task.isStage) {
            _item.taskType = "STAGE";
          } else {
          }
        }

        if (!_item.taskId && !_item.stageId) {
          _item.task.valid = false;
          _item.task.error = "Error!";
          isFormValid = isFormValid && false;
          invalidItems.push(`Row ${index + 1}: Missing task selection`);
        } else {
        }

        const { error: cpiError, valid: cpiValid } = this.handleValidation(
          _item.costPerItem,
        );
        _item.costPerItem["error"] = cpiError;
        _item.costPerItem["valid"] = cpiValid;
        _item.costPerItem.touched = true;

        if (!cpiValid) {
          isFormValid = false;
          invalidItems.push(`Row ${index + 1}: Invalid or missing cost (${_item.name.value || 'Unnamed'})`);
        }

        const { error: qtyError, valid: qtyValid } = this.handleValidation(
          _item.qty,
        );
        _item.qty.error = qtyError;
        _item.qty.valid = qtyValid;
        _item.qty.touched = true;

        if (!qtyValid) {
          isFormValid = false;
          invalidItems.push(`Row ${index + 1}: Invalid or missing quantity (${_item.name.value || 'Unnamed'})`);
        }

        return _item;
      });

      // Validate unique identifying key (task/stage, material item, qty)
      const seenLineKeys = new Map();
      updatedItems.forEach((_item, idx) => {
        const taskId = String(_item.taskId || _item.stageId || '');
        const itemId = String(_item.id || '');
        const qty = Number(_item.qty?.value !== undefined ? _item.qty.value : _item.qty) || 0;

        const lineKey = taskId && itemId ? `${taskId}_${itemId}_${qty}` : '';

        if (lineKey && seenLineKeys.has(lineKey)) {
          isFormValid = false;
          invalidItems.push(`Row ${idx + 1}: Duplicate item with same task, item and quantity (matches Row ${seenLineKeys.get(lineKey) + 1})`);
        } else if (lineKey) {
          seenLineKeys.set(lineKey, idx);
        }
      });

      if (!isFormValid) {
        this.setState({ selectedItems: updatedItems });
        const errorMessage = invalidItems.length > 0
          ? `Validation errors:\n${invalidItems.slice(0, 3).join('\n')}${invalidItems.length > 3 ? `\n... and ${invalidItems.length - 3} more` : ''}`
          : "Please fill in all required fields correctly. Check task selection, cost, and quantity for all items.";
        this.props.errorSnackBar(errorMessage);
      } else {
        this.props.submitForm(updatedItems);
      }
    }
  };

  // ========================= VALIDATION =========================
  // Basic validation for item fields
  handleValidation = ({ type, value }) => {
    let error = false;
    let valid = true;

    if (type === "number") {
      // Convert to number if it's a string
      const numValue = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : value;

      if (isNaN(numValue) || numValue < 0) {
        return { error: "Error!", valid: false };
      }
    }
    return { error, valid };
  };

  // ========================= PO LIST VIEW =========================
  // Trigger the view PO list action
  viewPoList = () => {
    this.props.viewPoList();
  };

  // ========================= INLINE ITEM HANDLING =========================
  // Toggle the inline item dialog state
  handleInlineItem = (index) => {
    this.setState((prevState) => ({
      addItemDialogState: !prevState.addItemDialogState,
      isInlineItem: true,
      inlineItemIndex: index,
    }));
  };

  // Add an inline item directly to the PO
  AddInlineItem = (data) => {
    if (data.noresult) {
      const { selectedItems } = this.state;
      const indexitem = selectedItems.findIndex((obj) => obj.id === "");
      selectedItems[indexitem].name.value = data.item;
      selectedItems[indexitem].id = uuidv4();
      selectedItems[indexitem].manufacturer = data.manufacturer || "";
      selectedItems[indexitem].partNo = data.partNo;
      selectedItems[indexitem].categoryId = "INLINE_ITEM";
      selectedItems[indexitem].catalog = "INLINE_ITEM";
      selectedItems[indexitem].unit.value = data.unit;
      selectedItems[indexitem].isInlineItem = true;
      selectedItems[indexitem].newItem = false;
      this.setState({ selectedItems, showAddItem: false });
    }
    this.setState({ checked: true });
  };

  // Submit the item form data to the server
  submitItemForm = (index = null) => {
    const { filterValue, selectedItems } = this.state;

    // If index is provided, use data from selectedItems[row], otherwise use filterValue
    let data;
    if (index !== null && selectedItems[index]) {
      const item = selectedItems[index];
      // Validate required fields before submitting
      if (!item.name.value || !item.costPerItem.value) {
        this.props.warningSnackBar(
          "Please fill in Item Description and Cost before saving to catalog",
        );
        return;
      }
      data = {
        categoryId: item.categoryId || filterValue["categoryId"] || "",
        item: item.name.value,
        cost: item.costPerItem.value,
        manufacturer: item.manufacturer || "",
        partNo: item.partNo || "",
        unit: item.unit?.value || "",
      };
    } else {
      // Fallback to filterValue for backward compatibility
      data = {
        categoryId: filterValue["categoryId"] || "",
        item: filterValue["item"] || "",
        cost: filterValue["costPerItem"] || "",
        manufacturer: filterValue["manufacturer"] || "",
        partNo: filterValue["partNo"] || "",
        unit: filterValue["unit"] || "",
      };
    }

    // Validate required fields
    if (!data.item || !data.cost) {
      this.props.warningSnackBar(
        "Please fill in Item Description and Cost before saving to catalog",
      );
      return;
    }

    if (!data.categoryId) {
      this.props.warningSnackBar(
        "Please select a Catalog before saving to catalog",
      );
      return;
    }

    this.props
      .addItem(data)
      .then((res) => {
        const newitemid = res.id;
        this.setState({ newitemid });
        if (res.id) {
          if (index !== null && selectedItems[index]) {
            // Update the specific row item
            const updatedItems = [...selectedItems];
            updatedItems[index].name.value = data.item;
            updatedItems[index].id = newitemid;
            updatedItems[index].manufacturer = data.manufacturer;
            updatedItems[index].partNo = data.partNo;
            updatedItems[index].categoryId = data.categoryId;
            updatedItems[index].unit.value = data.unit;
            updatedItems[index].newItem = true;
            updatedItems[index].isInlineItem = false;
            this.setState({
              selectedItems: updatedItems,
              showAddItem: false,
              filterValue: {},
            });
          } else if (filterValue.noresult) {
            // Fallback to old behavior
            const indexitem = selectedItems.findIndex((obj) => obj.id === "");
            if (indexitem >= 0) {
              const updatedItems = [...selectedItems];
              updatedItems[indexitem].name.value = data.item;
              updatedItems[indexitem].id = newitemid || uuidv4();
              updatedItems[indexitem].manufacturer = data.manufacturer;
              updatedItems[indexitem].partNo = data.partNo;
              updatedItems[indexitem].categoryId = data.categoryId;
              updatedItems[indexitem].unit.value = data.unit;
              updatedItems[indexitem].newItem = true;
              updatedItems[indexitem].isInlineItem = false;
              this.setState({
                selectedItems: updatedItems,
                showAddItem: false,
                filterValue: { ...filterValue, noresult: true },
              });
            }
          }
        }
      })
      .catch((error) => {
        console.error("Error saving item to catalog:", error);
        this.props.errorSnackBar("Failed to save item to catalog");
      });
  };

  // ========================= ROW AND PO CONSOLIDATION =========================
  // Add an empty row for a new item, if the current row is complete
  addEmptyRow = () => {
    const { selectedItems } = this.state;

    const hasIncompleteRow = selectedItems.some((item) => {
      // If item already has an ID, it's a saved item and is considered complete
      if (item.id && item.id !== "") {
        return false;
      }

      // For one-time items (inline items), categoryId is not required
      const isOneTimeItem =
        item.isInlineItem || item.categoryId === "INLINE_ITEM";

      // If item has name, cost, and qty, it's considered complete
      // CategoryId is only required for catalog items, not one-time items
      // Allow items without categoryId if they have essential fields filled (they can be one-time items)
      const hasEssentialFields =
        item.name.value &&
        item.costPerItem.value &&
        item.qty.value &&
        item.qty.value !== "0";
      const missingCategoryId =
        !isOneTimeItem && !item.categoryId && !hasEssentialFields;
      const missingCost = !item.costPerItem.value;
      const missingQty = !item.qty.value || item.qty.value === "0";
      return missingCategoryId || missingCost || missingQty;
    });

    if (hasIncompleteRow) {
      this.props.warningSnackBar(
        "Please complete the current row before adding a new one",
      );
      return;
    }

    const newItem = JSON.parse(JSON.stringify(this.state.item));
    newItem.id = "";
    // Inherit task and section from the last row if available, otherwise find the first selectable, non-disabled task
    const lastItem = Array.isArray(selectedItems) && selectedItems.length > 0
      ? selectedItems[selectedItems.length - 1]
      : null;

    if (lastItem && (lastItem.taskId || lastItem.stageId)) {
      newItem.taskId = lastItem.taskId || lastItem.stageId || "";
      newItem.taskName = lastItem.taskName || "";
      newItem.taskDescription = lastItem.taskDescription || "";
      newItem.taskType = lastItem.taskType || "";
      newItem.stageId = lastItem.stageId || "";
    } else {
      const firstSelectableTask = (this.props.tasksList || []).find(
        (task) => !task.isDisabled && !task.isSectionHeader && !task.value?.toString().startsWith("section-"),
      );
      newItem.taskId = firstSelectableTask ? firstSelectableTask.value : "";
      newItem.taskName = firstSelectableTask
        ? firstSelectableTask.label || firstSelectableTask.key
        : "";
      newItem.taskDescription = firstSelectableTask
        ? firstSelectableTask.taskDescription
        : "";
      newItem.taskType = firstSelectableTask ? firstSelectableTask.taskType : "";
      newItem.stageId = firstSelectableTask ? (firstSelectableTask.stageId || "") : "";
    }
    newItem.task = {
      error: "",
      valid: true,
    };
    newItem.categoryId = "";
    newItem.name.value = "";
    newItem.manufacturer = "";
    newItem.partNo = "";
    newItem.costPerItem.value = "";
    newItem.costPerItem.valid = true;
    newItem.costPerItem.error = "";
    newItem.unit.value = "";
    newItem.qty.value = "1";
    newItem.qty.valid = true;
    newItem.qty.error = "";
    newItem.total.value = 0;
    newItem.isInlineItem = false;

    selectedItems.push(newItem);

    // Calculate the last page to navigate to after adding the new item
    const { rowsPerPage } = this.state;
    const newTotalItems = selectedItems.length;
    const lastPage = Math.ceil(newTotalItems / rowsPerPage);

    this.setState({
      selectedItems,
      filterValue: {},
      tablePage: lastPage, // Jump to the last page to show the newly added item
    });
  };

  // Consolidate selected items into a single PO request
  consolidateToPO = () => {
    const { selectedItems } = this.state;
    const { poData } = this.props;

    if (!selectedItems.length) {
      this.props.warningSnackBar(
        "Please add items to the PO before consolidating",
      );
      return;
    }

    if (!poData.vendorId) {
      this.props.warningSnackBar("Please select a vendor");
      return;
    }

    const consolidatedItems = _.reduce(
      selectedItems,
      (result, item) => {
        const key = `${item.partNo || ""}-${item.manufacturer || ""}-${item.name.value || ""
          }`;

        if (!result[key]) {
          result[key] = {
            partNo: item.partNo || "",
            manufacturer: item.manufacturer || "",
            description: item.name.value || "",
            catalog:
              this.state.categories.items.find(
                (cat) => cat.value === item.categoryId,
              )?.key || "",
            unitCost: parseFloat(item.costPerItem.value) || 0,
            unit: item.unit.value || "",
            quantity: parseFloat(item.qty.value) || 0,
          };
        } else {
          result[key].quantity += parseFloat(item.qty.value) || 0;
        }

        return result;
      },
      {},
    );

    const itemsArray = Object.values(consolidatedItems);

    this.generateQuoteRequestPDF(itemsArray);
  };

  // ========================= TEMPLATE AND DOWNLOAD HANDLING =========================
  // Get template options for download
  gettemplate = async (type) => {
    let templateOptions = this.state.templateOptions;
    let newdata = [];
    let projectId = this.props.projectId;
    const templateData = await this.props.getGlobalSettingTemplateTypes(
      type,
      projectId,
    );
    for (let key in templateData) {
      newdata.push({
        value: templateData[key].id,
        key: templateData[key].name,
      });
    }
    templateOptions = newdata;
    this.setState({
      templateOptions,
    });
  };

  // Handle the download dialog state and template fetching
  handleDownloadDialog = (id = "", template = "") => {
    const { downloadData = {}, poRFQ } = this.state;
    downloadData.open = !downloadData.open;
    downloadData.id = id || poRFQ ? poRFQ.u_id : "";
    // downloadData.template = "subcontractor_payment_schedule";
    this.gettemplate("rfq_supplier");
    this.setState((prev) => ({
      ...prev,
      downloadData,
    }));
  };

  // Download the selected file in the chosen format
  downloadFile = async (fileFormat = "pdf", templatevalue) => {
    try {
      const { poRFQ } = this.state;
      if (fileFormat === "pdf" || fileFormat === "ALL") {
        await this.props
          .downloadProjectRFQ(
            poRFQ.u_id,
            fileFormat,
            templatevalue,
            this.props.poData.vendorId,
          )
          .catch((e) => console.error(e));
      }
      if (fileFormat === "docx" || fileFormat === "ALL") {
        await this.props
          .downloadProjectRFQ(
            poRFQ.u_id,
            fileFormat,
            templatevalue,
            this.props.poData.vendorId,
          )
          .catch((e) => console.error(e));
      }
      this.handleDownloadDialog();
    } catch (e) {
      console.error(e);
    }
  };

  // Generate a PDF for the quote request and trigger download
  generateQuoteRequestPDF = (items) => {
    try {
      const formData = new FormData();
      formData.set("vendorType", "Supplier");
      formData.set("description", "RFQ Request");
      formData.set("dueDate", new Date().toISOString());
      formData.set("draft", true);

      const formattedItems = items.map((item) => {
        return { ...item, qty: item.quantity, item: item.description };
      });

      for (let i = 0; i < formattedItems.length; i++) {
        formData.append("items[]", JSON.stringify(formattedItems[i]));
      }

      this.props
        .saveRFQ(
          formData,
          this.props.poData.projectId,
          "RFQ Saved Successfully",
        )
        .then((res) => {
          this.setState((prev) => ({
            ...prev,
            poRFQ: res,
          }));
          this.handleDownloadDialog(res.id, "rfq_supplier");
        });
    } catch (error) {
      console.error("Error generating PDF:", error);
      this.props.errorSnackBar("Failed to generate the PDF. Please try again.");
    }
  };

  // ========================= ITEM SELECTION OVERLAY =========================
  // Open the item selection overlay for choosing items to add to the PO
  openItemOverlay = (index, anchorEl) => {
    // Check if we have a valid DOM element
    if (!anchorEl || !anchorEl.getBoundingClientRect) {
      console.error("Invalid anchor element provided:", anchorEl);
      return;
    }

    const searchQuery = this.state.modalSearchQuery || "";

    this.setState({
      overlayOpen: true,
      overlayAnchorEl: anchorEl, // Use the direct DOM element
      isLoadingItems: true,
      currentItemIndex: index,
      overlayItemIndex: index,
      selectedItemIds: [],
      page: 0,
    });

    const params = {
      includeInactive: false,
      search: searchQuery,
    };

    this.props
      .getInventoryLists(params)
      .then((response) => {
        const rawItems = response.category || [];

        // Process items to ensure cost values are properly handled
        const processedItems = rawItems.map((item) => {
          // Remove any commas from cost string and ensure it's a number
          const cost = parseNumericValue(item.cost);

          return {
            ...item,
            cost: cost,
          };
        });

        const sortedItems = _.orderBy(processedItems, ["item"], ["asc"]);

        this.setState({
          availableItems: sortedItems,
          originalAvailableItems: sortedItems,
          isLoadingItems: false,
        });
      })
      .catch((error) => {
        console.error("Error loading inventory items:", error);
        this.props.errorSnackBar("Failed to load inventory items");
        this.setState({
          isLoadingItems: false,
          availableItems: [],
        });
      });

    /*
        const vendorId = this.props.poData && this.props.poData.vendorId;

        // First, try to get vendor-specific categories
        if (!vendorId) {
            this.props.warningSnackBar('No vendor selected');
            return;
        }
        // Use getVendorCategories to get all categories for this vendor
        this.props.getVendorCategories(vendorId)
            .then(vendorCategories => {
                if (!vendorCategories || !Array.isArray(vendorCategories) || vendorCategories.length === 0) return [];

                const categoryIds = vendorCategories.map(cat => cat.id).filter(Boolean).join(',');

                if (!categoryIds) return []

                const params = {
                    includeInactive: false,
                    search: searchQuery,
                    categoryId: categoryIds
                };

                return this.props.getInventoryLists(params);
            })
            .then(response => {
                const items = response.category || [];

                let filteredItems = items;
                this.setState({
                    availableItems: filteredItems,
                    originalAvailableItems: filteredItems,
                    isLoadingItems: false
                });

            })
            .catch(error => {
                console.error("Error loading inventory items:", error);
                this.props.errorSnackBar("Failed to load inventory items");
                this.setState({
                    isLoadingItems: false,
                    availableItems: []
                });
            });
        */
  };

  // Close the item selection overlay
  closeItemOverlay = () => {
    this.setState({
      overlayOpen: false,
      overlayAnchorEl: null,
      selectedItemIds: [],
      currentItemIndex: -1,
      overlayItemIndex: -1,
      modalSearchQuery: "",
      partNoSearch: "",
    });
  };

  // Handle selection of an item in the overlay
  handleItemSelection = (itemId) => {
    this.setState((prevState) => {
      const selectedIds = [...prevState.selectedItemIds];

      if (selectedIds.includes(itemId)) {
        return {
          selectedItemIds: selectedIds.filter((id) => id !== itemId),
        };
      }

      return {
        selectedItemIds: [...selectedIds, itemId],
      };
    });
  };

  // ========================= ROW FILLING AND ITEM ADDITION =========================
  // Fill a row with the selected item data and add to the selected items
  fillRowWithItem = (row, selectedItem, currentRowTaskInfo = null) => {
    // Fill basic item data
    row.name.value = selectedItem.item;
    row.id = selectedItem.id;
    row.manufacturer = selectedItem.manufacturer;
    row.partNo = selectedItem.partNo || selectedItem.part_no;
    row.categoryId = selectedItem.categoryId;
    row.costPerItem.value = parseNumericValue(selectedItem.cost);
    row.costPerItem.valid = true;
    row.costPerItem.error = "";
    row.unit.value = selectedItem.unit;
    row.qty.value = parseFloat(selectedItem.qty) || 1;
    row.qty.valid = true;
    row.qty.error = "";
    row.total.value =
      (parseFloat(selectedItem.qty) || 1) * row.costPerItem.value;
    row.isInlineItem = selectedItem.isInlineItem || false;

    // Use task info from current row context (for overlay items)
    if (currentRowTaskInfo && currentRowTaskInfo.taskId) {
      row.taskId = currentRowTaskInfo.taskId;
      row.taskName = currentRowTaskInfo.taskName;
      row.taskType = currentRowTaskInfo.taskType;
      row.task = {
        error: "",
        valid: true,
      };
    }

    // Add unit to newunitList if it exists
    if (selectedItem.unit) {
      this.setState((prev) => ({
        ...prev,
        newunitList: _.uniqBy(
          [
            ...prev.newunitList,
            ...[{ key: selectedItem.unit, value: selectedItem.unit }],
          ],
          "key",
        ),
      }));
    }
  };

  // Add selected items from the overlay to the PO
  addSelectedItemsToPO = () => {
    const {
      selectedItemIds,
      availableItems,
      currentItemIndex,
      selectedItems,
      item,
    } = this.state;

    if (selectedItemIds.length === 0) {
      this.props.warningSnackBar("Please select at least one item");
      return;
    }

    const selectedItemsToAdd = availableItems.filter((item) => {
      const key = `${item.id}${item.cost}`;
      return selectedItemIds.includes(key) || selectedItemIds.includes(item.id);
    });
    if (selectedItemsToAdd.length === 0) {
      this.props.warningSnackBar("Selected items not found");
      return;
    }

    // Validate currentItemIndex
    if (currentItemIndex < 0 || currentItemIndex >= selectedItems.length) {
      this.props.errorSnackBar("Invalid row selection");
      return;
    }

    let updatedItems = [...selectedItems];
    const currentItem = updatedItems[currentItemIndex];
    const task = (this.props.tasksList || []).find(
      (task) => task.value === currentItem?.taskId,
    );

    // Extract task info from current row to use for all items
    const currentRowTaskInfo =
      currentItem && currentItem.taskId
        ? {
          taskId: currentItem.taskId,
          taskName: task?.label || currentItem.taskName || "",
          taskType: task?.taskType || currentItem.taskType || "",
        }
        : null;

    const nonDuplicateItemsToAdd = selectedItemsToAdd.filter((itemToAdd) => {
      const targetToCheck = {
        id: itemToAdd.id,
        taskId: currentRowTaskInfo?.taskId || currentItem?.taskId,
        stageId: currentItem?.stageId,
        qty: itemToAdd.qty || 1,
      };
      return !this.isDuplicateLineItem(targetToCheck, selectedItems, currentItemIndex);
    });

    if (nonDuplicateItemsToAdd.length < selectedItemsToAdd.length) {
      this.props.warningSnackBar("An item with the same task, item and quantity already exists in the PO.");
    }
    if (nonDuplicateItemsToAdd.length === 0) {
      return;
    }

    let startIndex = 0;

    // Fill the current row with the first selected item
    this.fillRowWithItem(
      updatedItems[currentItemIndex],
      nonDuplicateItemsToAdd[0],
      currentRowTaskInfo,
    );
    startIndex = 1; // Start creating new rows from the second item

    // Create new rows for remaining items (if any)
    for (let i = startIndex; i < nonDuplicateItemsToAdd.length; i++) {
      const newItem = JSON.parse(JSON.stringify(item));
      this.fillRowWithItem(newItem, nonDuplicateItemsToAdd[i], currentRowTaskInfo);
      updatedItems.push(newItem);
    }

    this.setState({
      selectedItems: updatedItems,
      overlayOpen: false,
      selectedItemIds: [],
      currentItemIndex: -1,
      modalSearchQuery: "",
      filterValue: {},
    });

    this.props.successSnackBar(
      `${nonDuplicateItemsToAdd.length} item(s) added to PO successfully`,
    );
  };

  // ========================= SEARCH HANDLING =========================
  // Handle changes in the modal search input field
  handleModalSearchChange = (searchQuery, isPartNoSearch = false) => {
    // Update both search states to ensure consistency
    if (isPartNoSearch) {
      this.setState({
        modalSearchQuery: searchQuery,
        partNoSearch: searchQuery,
      });
    } else {
      this.setState({ modalSearchQuery: searchQuery });
    }

    clearTimeout(this.modalSearchTimer);
    this.modalSearchTimer = setTimeout(() => {
      this.searchModalItems(searchQuery, isPartNoSearch);
    }, 300);
  };

  // Search for items in the modal based on the query
  searchModalItems = (query, isPartNoSearch = false) => {
    const { currentItemIndex } = this.state;
    const currentItem =
      currentItemIndex >= 0 ? this.state.selectedItems[currentItemIndex] : null;

    this.setState({ isLoadingItems: true });

    if (!query || query.trim() === "") {
      const params = {
        includeInactive: false,
      };

      if (currentItem && currentItem.categoryId) {
        params.categoryId = currentItem.categoryId;
      }

      this.props
        .getInventoryLists(params)
        .then((response) => {
          const rawItems = response.category || [];

          // Process items to ensure cost values are properly handled
          const processedItems = rawItems.map((item) => {
            // Remove any commas from cost string and ensure it's a number
            const cost = parseNumericValue(item.cost);

            return {
              ...item,
              cost: cost,
            };
          });

          this.setState({
            availableItems: processedItems,
            originalAvailableItems: processedItems,
            isLoadingItems: false,
            page: 0,
          });
        })
        .catch((error) => {
          console.error("Error refreshing inventory items:", error);
          this.props.errorSnackBar("Failed to refresh inventory items");
          this.setState({ isLoadingItems: false });
        });

      return;
    }

    const params = {
      includeInactive: false,
    };

    // Use appropriate search parameter based on whether this is a part number search
    if (isPartNoSearch) {
      params.partNoFilter = query;
    } else {
      params.search = query;
    }

    if (currentItem && currentItem.categoryId) {
      params.categoryId = currentItem.categoryId;
    }

    this.props
      .getInventoryLists(params)
      .then((response) => {
        const rawItems = response.category || [];

        // Process items to ensure cost values are properly handled
        const processedItems = rawItems.map((item) => {
          // Remove any commas from cost string and ensure it's a number
          const cost = parseNumericValue(item.cost);

          return {
            ...item,
            cost: cost,
          };
        });

        // Sort the items appropriately based on search type
        const sortedItems = isPartNoSearch
          ? _.orderBy(processedItems, ["partNo"], ["asc"])
          : _.orderBy(processedItems, ["item"], ["asc"]);

        this.setState({
          availableItems: sortedItems,
          originalAvailableItems: sortedItems,
          isLoadingItems: false,
          page: 0,
        });
      })
      .catch((error) => {
        console.error("Error searching inventory items:", error);
        this.props.errorSnackBar("Failed to search inventory items");
        this.setState({ isLoadingItems: false });
      });
  };

  // Handle changes in the part number input field
  handlePartNoInputChange = (event, index) => {
    const partNoSearch = event && event.target ? event.target.value : "";
    const { selectedItems } = this.state;

    // Update the item's partNo value directly
    if (selectedItems[index]) {
      selectedItems[index].partNo = partNoSearch;
      this.setState({ selectedItems });
    }

    this.setState({ partNoSearch, modalSearchQuery: partNoSearch });

    // Clear any existing search timer
    clearTimeout(this.searchModalTimer);

    // Set up a new timer to open the overlay if there's content
    if (partNoSearch && partNoSearch.length > 2) {
      this.searchModalTimer = setTimeout(() => {
        this.openPartNoItemOverlay(index, event.target);
      }, 500);
    }

    return partNoSearch;
  };

  // Open the part number item overlay for selection
  openPartNoItemOverlay = (index, anchorEl) => {
    // Check if we have a valid DOM element
    if (!anchorEl || !anchorEl.getBoundingClientRect) {
      console.error("Invalid anchor element provided:", anchorEl);
      return;
    }

    const searchQuery = this.state.partNoSearch || "";

    this.setState(
      {
        overlayOpen: true,
        overlayAnchorEl: anchorEl,
        isLoadingItems: true,
        currentItemIndex: index,
        overlayItemIndex: index,
        selectedItemIds: [],
        page: 0,
      },
      () => {
        // Use searchModalItems with isPartNoSearch=true
        this.searchModalItems(searchQuery, true);
      },
    );
  };

  // Handle selection of a part number from the dropdown
  handlePartNoSelection = (index, newValue) => {
    if (!newValue || !newValue.value) return;

    const { itemsList } = this.state;
    const partNo = newValue.value;

    // Find the item with matching part number
    const matchingItem = itemsList.find((item) => item.partNo === partNo);

    if (!matchingItem) return;

    // Set up the state for addSelectedItemsToPO
    this.setState(
      {
        currentItemIndex: index,
        selectedItemIds: [matchingItem.id],
        availableItems: [matchingItem],
      },
      () => {
        // Call addSelectedItemsToPO to handle the item addition
        this.addSelectedItemsToPO();
      },
    );
  };

  // ========================= RENDERING =========================
  renderItemSelectionOverlay = () => {
    const {
      overlayOpen,
      overlayAnchorEl,
      availableItems,
      selectedItemIds,
      isLoadingItems,
      overlayItemIndex,
      modalSearchQuery,
      partNoSearch,
    } = this.state;

    // Determine if this is coming from a part number search
    const isPartNoSearch =
      partNoSearch !== "" && modalSearchQuery === partNoSearch;

    return (
      <ItemSelectionOverlay
        open={overlayOpen}
        anchorEl={overlayAnchorEl}
        items={availableItems}
        selectedItemIds={selectedItemIds}
        isLoading={isLoadingItems}
        onClose={this.closeItemOverlay}
        onSelectItem={this.handleItemSelection}
        onAddItems={() => {
          this.addSelectedItemsToPO();
          this.closeItemOverlay();
        }}
        onAddNewItem={() => {
          this.closeItemOverlay();
          setTimeout(() => {
            this.AddItemDialogHandler(overlayItemIndex, modalSearchQuery);
          }, 300);
        }}
        initialSearchValue={modalSearchQuery}
        isPartNoSearch={isPartNoSearch}
      />
    );
  };

  render() {
    const {
      categories,
      selectedItems,
      addItemDialogState,
      categoryId,
      isInlineItem,
      filterValue,
      itemName,
      tablePage,
      rowsPerPage,
    } = this.state;
    const { tasksList, permissions } = this.props;
    // tasksList already comes formatted from create.jsx with sections and disabled options
    const formattedTasksList = tasksList || [];
    // ========================= PAGINATION CALCULATIONS =========================
    const totalItems = selectedItems.length;
    const totalPages = Math.ceil(totalItems / rowsPerPage);
    const currentPage = Math.min(tablePage, totalPages || 1);
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = Math.min(startIndex + rowsPerPage, totalItems);
    const pagedItems = selectedItems.slice(startIndex, endIndex);

    return (
      <>
        <DownloadDialog
          {...this.state.downloadData}
          downloadFile={this.downloadFile}
          templateOptions={this.state.templateOptions}
          downloadMudleName="Po RFQ"
        />
        {this.renderItemSelectionOverlay()}
        <AddItemDialog
          open={addItemDialogState}
          submitHandler={this.AddItemDialogClose}
          dialogClose={() =>
            this.setState({ addItemDialogState: false, inlineItemIndex: -1 })
          }
          categoryId={categoryId}
          itemName={itemName || filterValue}
          isInlineItem={isInlineItem}
          writePermission={idx(permissions, (_) => _.catalog) === 2 || false}
        />
        <div className="add-po-table material-list-block mt-3">
          <div className="table-responsive add-material-po-table-responsive">
            <Table
              bordered
              className="table-create table-material tbl-fixed add-material-po-table"
            >
              <thead>
                <tr>
                  {formattedTasksList.length > 0 && (
                    <th className="select-task-column">Select Task</th>
                  )}
                  <th className="catalog-width">Catalog</th>
                  <th className="category-width">Category</th>
                  <th className="partno-width">Part No</th>
                  <th className="item-width">Item Description</th>
                  <th className="text-right uom-width">UoM</th>
                  <th className="cost-width">Cost($)</th>
                  <th className="qty-width">Quantity</th>
                  <th className="text-right total-width">Total ($)</th>
                  <th className="text-right actions-width">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagedItems &&
                  Array.isArray(pagedItems) &&
                  pagedItems.map((_item, pageIndex) => {
                    const absoluteIndex = startIndex + pageIndex;
                    const itemKey = `${_item.id}-${absoluteIndex}`;
                    return (
                      <tr key={itemKey}>
                        {formattedTasksList.length > 0 && (
                          // Task Select Dropdown
                          <td className="select-task-column">
                            <TaskMultiSelect
                              filterVariationsByStatus={true}
                              items={formattedTasksList}
                              value={
                                (_item.taskId || _item.stageId)
                                  ? formattedTasksList.find(
                                    (task) => task.value == (_item.taskId || _item.stageId),
                                  ) || (_item.taskName ? { value: _item.taskId || _item.stageId, label: _item.taskName } : null)
                                  : null
                              }
                              onChangeHandler={(selectedOption) =>
                                this.changeTaskHandler(absoluteIndex, selectedOption)
                              }
                              placeholder="Select Task"
                              isMulti={false}
                              dropdownWidth="400px"
                              isSearchable={true}
                            />
                          </td>
                        )}
                        {/* Catalog Input Field / Selector */}
                        <td className="catalog-width">
                          {_item.id &&
                            (getIsInlineItem(_item)
                              ? INLINE_CATALOG_DISPLAY_NAME
                              : categories.items.find(
                                (item) => item.value === _item.categoryId,
                              )?.key || "")}
                          {!_item.isInlineItem && !_item.id && (
                            <div className="form-group">
                              <div
                                className="input-block input-block-sm autocomplete-block autocomplete-block-ch mt-1"
                                data-row-index={absoluteIndex}
                              >
                                <Autocomplete
                                  value={_item.catalog}
                                  onChange={(event, newValue) => {
                                    if (typeof newValue === "string") {
                                    } else if (
                                      newValue &&
                                      newValue.inputValue
                                    ) {
                                    } else if (newValue) {
                                      this.handleCategoryChange(
                                        absoluteIndex,
                                        newValue.value,
                                        _item.id,
                                        newValue.key,
                                      );
                                      this.addFilter(newValue.key, "catalog");
                                      this.addFilter(
                                        newValue.value,
                                        "categoryId",
                                      );
                                    } else {
                                      this.addFilter("", "catalog");
                                    }
                                  }}
                                  filterOptions={(options, params) => {
                                    return filter(options, params);
                                  }}
                                  selectOnFocus
                                  handleHomeEndKeys
                                  clearOnEscape={false}
                                  clearOnBlur={false}
                                  clearText={true}
                                  id="free-solo-with-text-demo"
                                  options={categories.items}
                                  PopperComponent={(props) => (
                                    <Popper
                                      {...props}
                                      className="catalog-dropdown-popper"
                                    />
                                  )}
                                  ListboxProps={{
                                    className: "text-wrap",
                                  }}
                                  getOptionLabel={(option) => {
                                    if (typeof option === "string") {
                                      return option;
                                    }
                                    if (option.inputValue) {
                                      return option.inputValue;
                                    }
                                    return option.key;
                                  }}
                                  renderOption={(props, option) => (
                                    <li {...props}>{option.key}</li>
                                  )}
                                  freeSolo
                                  renderInput={(params) => {
                                    // Save the input ref for this specific catalog field
                                    return (
                                      <TextField
                                        {...params}
                                        variant="standard"
                                        InputProps={{
                                          ...params.InputProps,
                                          inputRef: (el) => {
                                            // Store the actual DOM element
                                            if (el) {
                                              this.inputRefs[
                                                `catalog-${absoluteIndex}`
                                              ] = el;
                                            }
                                          },
                                        }}
                                      />
                                    );
                                  }}
                                  onInputChange={(event, newValue) => {
                                    this.addFilter(
                                      newValue.key || newValue,
                                      "catalog",
                                    );
                                    this.addFilter(newValue.value, "catalogid");
                                  }}
                                />
                              </div>
                            </div>
                          )}
                          {_item.isInlineItem && <span>NON CATALOG</span>}
                        </td>
                        {/* Category Input Field */}
                        <td className="category-width">
                          {_item.id && _item.manufacturer}
                          {!_item.isInlineItem && !_item.id && (
                            <div className="form-group">
                              <div className="input-block input-block-sm autocomplete-block autocomplete-block-ch mt-1">
                                <Autocomplete
                                  value={_item.manufacturer || ""}
                                  onChange={(event, newValue) => {
                                    const { selectedItems } = this.state;
                                    let manufacturerValue = "";

                                    if (typeof newValue === "string") {
                                      // User typed a custom value (freeSolo)
                                      manufacturerValue = newValue;
                                    } else if (
                                      newValue &&
                                      newValue.inputValue
                                    ) {
                                      // User typed a new value (inputValue case)
                                      manufacturerValue = newValue.inputValue;
                                    } else if (newValue) {
                                      // User selected from dropdown
                                      manufacturerValue =
                                        newValue.key || newValue.value || "";
                                    } else {
                                      // User cleared the field (newValue is null)
                                      manufacturerValue = "";
                                    }

                                    // Update the item's manufacturer field - create new array to ensure state update
                                    if (selectedItems[absoluteIndex]) {
                                      const updatedItems = [...selectedItems];
                                      updatedItems[absoluteIndex] = {
                                        ...updatedItems[absoluteIndex],
                                        manufacturer: manufacturerValue,
                                      };
                                      this.setState({
                                        selectedItems: updatedItems,
                                      });
                                    }

                                    // Also update filter for search purposes
                                    this.addFilter(
                                      manufacturerValue,
                                      "manufacturer",
                                    );
                                  }}
                                  onInputChange={(event, newInputValue) => {
                                    // Update manufacturer value as user types (for freeSolo custom values)
                                    const { selectedItems } = this.state;
                                    if (
                                      selectedItems[absoluteIndex] &&
                                      newInputValue !== null &&
                                      newInputValue !== undefined
                                    ) {
                                      const updatedItems = [...selectedItems];
                                      updatedItems[absoluteIndex] = {
                                        ...updatedItems[absoluteIndex],
                                        manufacturer: newInputValue,
                                      };
                                      this.setState({
                                        selectedItems: updatedItems,
                                      });
                                    }
                                    // Also update filter for search purposes
                                    this.addFilter(
                                      newInputValue,
                                      "manufacturer",
                                    );
                                  }}
                                  filterOptions={(options, params) => {
                                    return filter(options, params);
                                  }}
                                  selectOnFocus
                                  handleHomeEndKeys
                                  clearOnEscape={false}
                                  clearOnBlur={false}
                                  clearText={true}
                                  id="free-solo-with-text-demo"
                                  options={this.manufacturerDropDown()}
                                  getOptionLabel={(option) => {
                                    if (typeof option === "string") {
                                      return option;
                                    }
                                    if (option.inputValue) {
                                      return option.inputValue;
                                    }
                                    return option.key;
                                  }}
                                  renderOption={(props, option) => (
                                    <li {...props}>{option.key}</li>
                                  )}
                                  freeSolo
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      variant="standard"
                                      label="Manufacturer"
                                    />
                                  )}
                                  PopperComponent={(props) => (
                                    <Popper
                                      {...props}
                                      className="catalog-dropdown-popper"
                                    />
                                  )}
                                  ListboxProps={{
                                    className: "text-wrap",
                                  }}
                                />
                              </div>
                            </div>
                          )}
                        </td>
                        {/* Part No Input Field */}
                        <td className="partno-width">
                          {_item.id && _item.partNo}
                          {!_item.isInlineItem && !_item.id && (
                            <div className="form-group">
                              <div className="input-block input-block-sm autocomplete-block autocomplete-block-ch mt-1">
                                <CustomInput
                                  className="input-block-sm"
                                  fullWidth
                                  value={_item.partNo || ""}
                                  inputProps={{
                                    ref: (el) => {
                                      if (el) {
                                        this.inputRefs[`partNo-${absoluteIndex}`] = el;
                                      }
                                    },
                                  }}
                                  onChange={(e) => {
                                    this.handlePartNoInputChange(e, absoluteIndex);
                                  }}
                                />
                              </div>
                            </div>
                          )}
                        </td>
                        {/* Item Description Input Field */}
                        <td className="item-width">
                          <div className="flex-grow-1 text-wrap">
                            {_item.isInlineItem && _item.name.value}

                            {!_item.isInlineItem &&
                              _item.id &&
                              _item.name.value}

                            {!_item.isInlineItem && !_item.id && (
                              <div className="form-group">
                                <CustomInput
                                  value={_item.name.value}
                                  className="input-block-sm"
                                  fullWidth
                                  inputProps={{
                                    ref: (el) => {
                                      if (el) {
                                        this.inputRefs[`item-${absoluteIndex}`] = el;
                                      }
                                    },
                                  }}
                                  onChange={(e) => {
                                    const query = e.target.value;
                                    _item.name.value = query;

                                    if (query && query.length > 0) {
                                      this.setState({
                                        modalSearchQuery: query,
                                        search: query,
                                      });

                                      clearTimeout(this.searchModalTimer);
                                      this.searchModalTimer = setTimeout(() => {
                                        const inputEl =
                                          this.inputRefs[`item-${absoluteIndex}`];
                                        if (inputEl) {
                                          this.openItemOverlay(absoluteIndex, inputEl);
                                        } else {
                                          console.error(
                                            "No input element found for item-" +
                                            absoluteIndex,
                                          );
                                        }
                                        this.openItemOverlay(absoluteIndex, e.target);
                                      }, 500);
                                    }
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        </td>
                        {/* Unit of Measure (UoM) Field */}
                        <td className="text-right uom-width">
                          {_item.id && _item.unit?.value && (
                            <span>{_item.unit.value}</span>
                          )}
                          {!_item.id && (
                            <div className="form-group">
                              <SelectInput
                                className="input-block-sm"
                                name="unit"
                                value={_item.unit?.value || ""}
                                items={this.state.newunitList || []}
                                placeholder="Select Unit"
                                customClassName="w-100"
                                onChange={(e) => {
                                  const { selectedItems } = this.state;
                                  const updatedItems = [...selectedItems];
                                  if (updatedItems[absoluteIndex]) {
                                    updatedItems[absoluteIndex] = {
                                      ...updatedItems[absoluteIndex],
                                      unit: {
                                        ...updatedItems[absoluteIndex].unit,
                                        value: e.target.value,
                                      },
                                    };
                                    this.setState({
                                      selectedItems: updatedItems,
                                    });
                                  }
                                }}
                              />
                            </div>
                          )}
                        </td>
                        {/* Cost Input Field */}
                        <td className="cost-width">
                          <NumberInput
                            className="input-block-sm"
                            type="text"
                            value={
                              _item.costPerItem.value === ""
                                ? ""
                                : typeof _item.costPerItem.value === "number"
                                  ? numberFormat(_item.costPerItem.value, true)
                                  : _item.costPerItem.value
                            }
                            name="costPerItem"
                            error={_item.costPerItem.error}
                            valid={_item.costPerItem.valid}
                            touched={_item.costPerItem.touched}
                            onChange={(e) => this.itemChangeHandler(absoluteIndex, e)}
                            customInputClass="text-right"
                            onBlur={(e) => {
                              if (e.target.value === "") {
                                _item.costPerItem.value = 0;
                              } else {
                                const numValue = parseNumericValue(
                                  e.target.value,
                                );
                                _item.costPerItem.value = numValue;
                              }

                              // Recalculate total
                              const qty = parseFloat(_item.qty.value) || 0;
                              _item.total.value = qty * _item.costPerItem.value;

                              this.setState((prevState) => ({
                                selectedItems: [...prevState.selectedItems],
                              }));
                            }}
                          />
                        </td>
                        {/* Quantity Input Field */}
                        <td className="qty-width">
                          <NumberInput
                            className="input-block-sm"
                            type="text"
                            {..._item.qty}
                            onChange={(e) => this.itemChangeHandler(absoluteIndex, e)}
                            onBlur={(e) => {
                              if (e.target.value === "") {
                                _item.qty.value = 0;
                              } else {
                                const numValue = parseNumericValue(
                                  e.target.value,
                                );
                                _item.qty.value = numValue;
                              }

                              const cost =
                                parseFloat(_item.costPerItem.value) || 0;
                              _item.total.value = _item.qty.value * cost;

                              this.setState((prevState) => ({
                                selectedItems: [...prevState.selectedItems],
                              }));
                            }}
                          />
                        </td>

                        {/* Total ($) output text */}
                        <td className="text-right total-width">
                          {toUSD(_item.total.value)}
                        </td>
                        {/* Actions field buttons */}
                        <td className="text-right actions-width">
                          {this.state.filterValue?.noresult && (
                            <div className="d-flex justify-content-end align-items-center">
                              {!_item.id && (
                                <>
                                  <Tooltip
                                    title="Save this item to the catalog for future use"
                                    placement="top"
                                  >
                                    <span
                                      className="action-edit"
                                      onClick={() => this.submitItemForm(absoluteIndex)}
                                    >
                                      <SaveIcon />
                                    </span>
                                  </Tooltip>
                                  {/* <Tooltip
                                    title="When checked, saves to catalog. When unchecked, adds as one-time item only"
                                    placement="top"
                                  >
                                    <div className="table-checkbox mx-2">
                                      <Form.Check
                                        type={"checkbox"}
                                        value={this.state.checked}
                                        defaultChecked={false}
                                        onChange={() =>
                                          this.setChecked(!this.state.checked)
                                        }
                                      />
                                    </div>
                                  </Tooltip> */}
                                </>
                              )}
                              <Tooltip title="Remove this row" placement="top">
                                <span
                                  className="action-edit"
                                  onClick={() => this.removeItem(absoluteIndex)}
                                >
                                  <Delete className="deleteicon" />
                                </span>
                              </Tooltip>
                            </div>
                          )}

                          {!this.state.filterValue?.noresult && (
                            <div className="d-inline-flex justify-content-center align-items-center">
                              <Tooltip title="Remove this row" placement="top">
                                <Button
                                  variant="text"
                                  className="p-0"
                                  onClick={() => this.removeItem(absoluteIndex)}
                                  style={{ minWidth: "auto" }}
                                >
                                  <Delete className="deleteicon" />
                                </Button>
                              </Tooltip>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </Table>
          </div>

          {/* ========================= PAGINATION & ADD ITEM ========================= */}
          {totalItems > 0 && (
            <div className="col-12 pt-3 d-flex justify-content-center align-items-center gap-3">
              {totalItems > rowsPerPage && (
                <div className="pagination-component-wrapper">
                  <PaginationComponent
                    itemsCount={totalItems}
                    itemsPerPage={rowsPerPage}
                    currentPage={currentPage}
                    setCurrentPage={this.setTablePage}
                  />
                </div>
              )}
              <div className="pagination-dropdown">
                <SelectInput
                  name="rowsPerPage"
                  value={rowsPerPage}
                  items={[
                    { key: "10 per page", value: 10 },
                    { key: "25 per page", value: 25 },
                    { key: "50 per page", value: 50 },
                    { key: "100 per page", value: 100 },
                  ]}
                  onChange={this.handleRowsPerPageChange}
                  customClassName="dynamic-table-select"
                  className="custom-select-dropdown"
                />
              </div>
            </div>
          )}

          <div className="d-flex justify-content-center mt-3">
            {formattedTasksList.length > 0 ? (
              <Tooltip title="Add a new row" placement="top">
                <Button
                  variant="text"
                  onClick={this.addEmptyRow}
                  className="p-0 d-flex justify-content-center align-items-center"
                  style={{
                    minWidth: "auto",
                  }}
                >
                  <AddIcon
                    style={{ color: "#28a745", fontSize: "2rem" }}
                  />
                  <span className="ms-2">Add Item</span>
                </Button>
              </Tooltip>
            ) : (
              <p>Select tasks first</p>
            )}
          </div>
        </div>

        <div className="col-12 d-flex mt-3 justify-content-end">
          <Button
            type="submit"
            onClick={this.submitForm}
            className="me-3 secondarybtn"
            data-cy="save-button"
          >
            SAVE
          </Button>
          {selectedItems &&
            selectedItems.length > 0 &&
            selectedItems.some(
              (item) =>
                item.category !== "" &&
                item.partNo !== "" &&
                item.manufacturer !== "" &&
                item.costPerItem.value !== "" &&
                item.qty.value !== "" &&
                item.name.value !== "",
            ) && (
              <Button
                type="button"
                onClick={this.consolidateToPO}
                className="me-3 create-po-rfq-button"
                data-cy="generate-rfq-button"
              >
                <GetAppIcon className="me-1" /> GENERATE RFQ
              </Button>
            )}
          <Button
            type="submit"
            onClick={this.viewPoList}
            className="secondarybtn cancelbtn"
            data-cy="cancel-button"
          >
            CANCEL
          </Button>
        </div>
      </>
    );
  }
}

AddMaterialPO.defaultProps = {
  poData: {},
  taskItems: [],
  onItemRemove: null,
};

AddMaterialPO.propTypes = {
  poData: PropTypes.object,
  taskItems: PropTypes.array,
  onItemRemove: PropTypes.func,
};

const mapStateToProps = (state) => ({
  permissions: state.user.userData.permissions,
  categoryList: state.catalog.catalogs,
});

const mapDispatchToProps = (dispatch) => ({
  addItem: bindActionCreators(addItem, dispatch),
  getInventoryLists: bindActionCreators(getInventoryLists, dispatch),
  getVendorCategories: bindActionCreators(getVendorCategories, dispatch),
  warningSnackBar: bindActionCreators(warningSnackBar, dispatch),
  successSnackBar: bindActionCreators(successSnackBar, dispatch),
  errorSnackBar: bindActionCreators(errorSnackBar, dispatch),
  getUnits: bindActionCreators(getUnits, dispatch),
  listCategories: bindActionCreators(listCategories, dispatch),
  saveRFQ: bindActionCreators(saveRFQ, dispatch),
  getGlobalSettingTemplateTypes: bindActionCreators(
    getGlobalSettingTemplateTypes,
    dispatch,
  ),
  downloadProjectRFQ: bindActionCreators(downloadProjectRFQ, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(AddMaterialPO);
